import {
  SystemStyleObject,
  TableCellProps as ChakraTableCellProps,
  TableColumnHeaderProps as ChakraTableHeaderProps,
} from "@chakra-ui/react";
import {
  Cell,
  RowData,
  IdentifiedColumnDef,
  CellContext,
  TableMeta,
  HeaderContext,
} from "@tanstack/react-table";
import { useRef } from "react";
import type { ComponentProps } from ".";

function getLatest<T>(property: string, prev: T, current: T) {
  if (prev && current) {
    // eslint-disable-next-line no-console
    console.warn(
      `Attempting to set the \`${property}\` multiple times! ` +
        "Only using the most recent version.",
    );
  }
  return current || prev;
}

declare module "@tanstack/table-core" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    cellProps?: Omit<ChakraTableCellProps, "key">;
    headerProps?: Omit<ChakraTableHeaderProps, "key">;
    footerProps?: Omit<ChakraTableCellProps, "key">;
    pinned?: "left" | "right";
    props?: Omit<ChakraTableHeaderProps & ChakraTableCellProps, "key">;
    getCellProps?: (
      cell: Cell<TData, TValue>,
    ) => Omit<ChakraTableCellProps, "key">;
  }
}

export function useChakraReactTableProps<D extends RowData>(
  propsArray: (Partial<ComponentProps<D>> | null | undefined)[],
): Partial<ComponentProps<D>> {
  return propsArray.reduce<Partial<ComponentProps<D>>>(
    (prev, props) => ({
      colgroupElement: getLatest(
        "colgroupElement",
        prev.colgroupElement,
        props?.colgroupElement,
      ),
      loadingSpinnerProps: {
        ...prev.loadingSpinnerProps,
        ...props?.loadingSpinnerProps,
      },
      tableBodyProps: { ...prev.tableBodyProps, ...props?.tableBodyProps },
      tableHeadProps: { ...prev.tableHeadProps, ...props?.tableHeadProps },
      tableFootProps: { ...prev.tableFootProps, ...props?.tableFootProps },
      tableProps: { ...prev.tableProps, ...props?.tableProps },
      getCellProps: (cell) => ({
        ...prev.getCellProps?.(cell),
        ...props?.getCellProps?.(cell),
      }),
      getColumnProps: (column) => ({
        ...prev.getColumnProps?.(column),
        ...props?.getColumnProps?.(column),
      }),
      getHeaderGroupProps: (headerGroup) => ({
        ...prev.getHeaderGroupProps?.(headerGroup),
        ...props?.getHeaderGroupProps?.(headerGroup),
      }),
      getHeaderProps: (header) => ({
        ...prev.getHeaderProps?.(header),
        ...props?.getHeaderProps?.(header),
      }),
      getFooterGroupProps: (footerGroup) => ({
        ...prev.getFooterGroupProps?.(footerGroup),
        ...props?.getFooterGroupProps?.(footerGroup),
      }),
      getFooterProps: (footer) => ({
        ...prev.getFooterProps?.(footer),
        ...props?.getFooterProps?.(footer),
      }),
      getRowProps: (row) => ({
        ...prev.getRowProps?.(row),
        ...props?.getRowProps?.(row),
      }),
      noRowsPlaceholderProps: {
        ...prev.noRowsPlaceholderProps,
        ...props?.noRowsPlaceholderProps,
      },
      noRowsPlaceholderRowProps: {
        ...prev.noRowsPlaceholderRowProps,
        ...props?.noRowsPlaceholderRowProps,
      },
    }),
    {},
  );
}

export function makeNotReadonly<D extends RowData>(
  data: readonly D[] | D[],
): D[] {
  return data as unknown as D[];
}

export function usePrepApolloData<D extends RowData>(
  data: readonly D[] | D[] | null | undefined,
): D[] {
  const { current: defaultData } = useRef<D[]>([]);
  if (!data) return defaultData;
  return makeNotReadonly(data);
}

/**
 * The `createColumnHelper` function from `@tanstack/react-table` attempts to
 * recurse objects to find all possible keys, which causes problems when row
 * data contain more complex objects, leading TS to crash. This avoids such
 * crashes by not recursing objects and consequently only supports keys that are
 * one level deep.
 */
export function createFlatAccessorKeyColumnHelper<TData extends RowData>() {
  return <TAccessor extends keyof TData, TValue extends TData[TAccessor]>(
    accessor: TAccessor,
    column: IdentifiedColumnDef<TData, TValue>,
  ) => ({ ...column, accessorKey: accessor });
}

/**
 * The `createColumnHelper` function from `@tanstack/react-table` attempts to
 * recurse objects to find all possible keys, which causes problems when row
 * data contain more complex objects, leading TS to crash. This avoids such
 * crashes by not recursing objects and consequently only supports keys that are
 * one level deep.
 */
export function createFlatAccessorFnColumnHelper<TData extends RowData>() {
  return <TValue>(
    accessor: (row: TData) => TValue,
    column: IdentifiedColumnDef<TData, TValue> & { id: string },
  ) => ({ ...column, accessorFn: accessor });
}

export function createTableMetaHelper<
  D extends RowData,
  TMeta extends TableMeta<D> | undefined = TableMeta<D> | undefined,
>() {
  return {
    cell: <DCell extends RowData = D, TValue = unknown>(
      cell: CellContext<DCell, TValue> | HeaderContext<DCell, TValue>,
    ) => cell.table.options.meta as TMeta,
  };
}

export const pinnedColumnStyles = {
  '[data-pinning="left"][data-pinning-index-inside="0"]': {
    boxShadow: "inset -1px 0px 0px rgba(0,0,0,.1)",
  },
  '[data-pinning="right"]': {
    boxShadow: "inset 1px 0px 0px rgba(0,0,0,.1)",
    borderRight: 0,
  },
};

export function combineWithPinnedColumnBoxShadow(
  boxShadow: string,
): ChakraTableCellProps {
  const withPinnedStyles = Object.keys(pinnedColumnStyles).reduce<{
    [property: string]: SystemStyleObject;
  }>((acc, selector) => {
    const pinnedStyles =
      pinnedColumnStyles[selector as keyof typeof pinnedColumnStyles];
    if (!pinnedStyles.boxShadow) return acc;
    acc[`&${selector}`] = {
      boxShadow: `${boxShadow}, ${pinnedStyles.boxShadow}`,
    };
    return acc;
  }, {});

  return { boxShadow, sx: withPinnedStyles };
}
